// Library Methods
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import ExternalExamIframe from "src/components/containers/shared/ExternalExamIframe/ExternalExamIframe";
import OCTComparisonImage from "./OCTComparisonImage/OCTComparisonImage";
import LegacyVFExam from "./LegacyVFExam/ExternalExamReportSection/LegacyVFExam";
import { ZoomInIcon, ZoomOutIcon } from "src/assets/icons";

// Styles
import {
  ThemeProvider as LightThemeProvider,
  useTheme,
} from "@mui/material/styles";

// Types
import {
  ExamSectionReportType,
  IndividualExternalExamType,
} from "src/services/exams/types";

// Config
import { priorityLanguageMap } from "src/config/languages";

// Contexts
import { ReportSectionContext } from "src/contexts/reports/ReportSectionContext";
import { ReportContext } from "src/contexts/reports/ReportContext";
import { useExternalExamsContext } from "src/contexts/exams/ExternalExamsContext";

// Utils
import { setupTheme } from "src/utils/theme/themeHelper";
import { resolveExternalExamViewer } from "src/utils/exams/externalExamViewerHelper";

const ZOOM_STEP = 10;
const ZOOM_MIN = 50;
const ZOOM_MAX = 150;

type ComparisonImageType = {
  selectedExam: IndividualExternalExamType;
  hideToolbar?: boolean;
  octOverviewWidth?: string;
  octFillOverviewHeight?: boolean;
  octCompactSlider?: boolean;
  zoomIframe?: boolean;
  showZoomControls?: boolean;
};

const ComparisonImage = ({
  selectedExam,
  hideToolbar = false,
  octOverviewWidth,
  octFillOverviewHeight = false,
  octCompactSlider = false,
  zoomIframe = false,
  showZoomControls = false,
}: ComparisonImageType) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const { selectedPatient } = useExternalExamsContext();

  const isLegacyVisualField = useMemo(
    () =>
      selectedExam?.examType === "VisualField" &&
      selectedExam?.data?.algorithmType,
    [selectedExam?.examType, selectedExam?.data?.algorithmType]
  );

  const [zoomLevel, setZoomLevel] = useState(() =>
    zoomIframe ? 70 : undefined
  );

  // undefined means "fit to width" (the native viewer's own default) rather
  // than a specific percentage -- treat it as 100% for the purposes of the
  // first zoom step, same as the viewer itself would render it.
  const handleZoomIn = () =>
    setZoomLevel((level) => Math.min(ZOOM_MAX, (level ?? 100) + ZOOM_STEP));
  const handleZoomOut = () =>
    setZoomLevel((level) => Math.max(ZOOM_MIN, (level ?? 100) - ZOOM_STEP));

  // The legacy VF report isn't styled for dark mode, so it's locked to
  // light regardless of the app's theme -- same approach as the report pages.
  const languageKey = i18n.language as keyof typeof priorityLanguageMap;
  const lightTheme = useMemo(
    () => setupTheme(theme.direction, languageKey, "light"),
    [theme.direction, languageKey]
  );

  const viewer = resolveExternalExamViewer(selectedExam);

  if (viewer === "frames" || viewer === "image") {
    return (
      <OCTComparisonImage
        images={selectedExam.images ?? []}
        fallbackImageUrl={selectedExam.examFileUrl}
        overviewWidth={octOverviewWidth}
        fillOverviewHeight={octFillOverviewHeight}
        compactSlider={octCompactSlider}
      />
    );
  }

  if (viewer === "none") {
    if (isLegacyVisualField) {
      return (
        <ReportContext.Provider
          value={{
            patient: selectedPatient,
            exam: undefined,
            externalExam: selectedExam,
            progressionAnalysis: undefined,
            reportData: {},
            refetchProgressionAnalysisComment: async () => {},
            refetchExamComment: async () => {},
            getReportInfoData: async () => {},
          }}
        >
          <ReportSectionContext.Provider
            value={{
              sectionId: selectedExam?.id ?? "",
              eyeType: selectedExam?.eye ?? "",
              commentData: [],
              duration: "",
              durationStartTime: "",
              examReport: {} as ExamSectionReportType,
            }}
          >
            <LightThemeProvider theme={lightTheme}>
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  overflow: "auto",
                  p: "20px",
                  backgroundColor: "#FFF",
                  color: "#202338",
                }}
              >
                <LegacyVFExam externalExam={selectedExam} />
              </Box>
            </LightThemeProvider>
          </ReportSectionContext.Provider>
        </ReportContext.Provider>
      );
    } else {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Typography sx={{ color: "#000", textAlign: "center" }}>
            {t("No image preview available.")}
          </Typography>
        </Box>
      );
    }
  }

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        height: "100%",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <ExternalExamIframe
        examUrl={selectedExam.examFileUrl}
        height="100%"
        hideToolbar={hideToolbar}
        zoom={zoomLevel}
      />

      {showZoomControls && (
        <Box
          sx={{
            position: "absolute",
            top: "8px",
            left: "8px",
            display: "flex",
            alignItems: "center",
            gap: "4px",
            px: "4px",
            borderRadius: "4px",
            backgroundColor: "rgba(13, 15, 19, 0.78)",
          }}
        >
          <IconButton
            disableRipple
            disableFocusRipple
            aria-label={t("Zoom out")}
            onClick={handleZoomOut}
            disabled={zoomLevel !== undefined && zoomLevel <= ZOOM_MIN}
            sx={{ color: "#FFF", padding: "4px" }}
          >
            <ZoomOutIcon width="16px" height="16px" />
          </IconButton>
          <Typography
            display={{ xs: "none", sm: "flex" }}
            justifyContent="center"
            fontSize="12px"
            color="#FFF"
            minWidth="24px"
          >
            {zoomLevel !== undefined ? `${zoomLevel}%` : t("Fit")}
          </Typography>
          <IconButton
            disableRipple
            disableFocusRipple
            aria-label={t("Zoom in")}
            onClick={handleZoomIn}
            disabled={zoomLevel !== undefined && zoomLevel >= ZOOM_MAX}
            sx={{ color: "#FFF", padding: "4px" }}
          >
            <ZoomInIcon width="16px" height="16px" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default ComparisonImage;
